/**
 * The RPC envelope layered over the embed postMessage channel.
 *
 * Like `embedMessages.ts`, this file is duplicated verbatim in both repositories:
 *
 *   veriform-desktop/src/renderer/WebEmbed/nativeBridgeMessages.ts
 *   veriform-web/src/frontend/embed/nativeBridgeMessages.ts
 *
 * There is no shared package, so the two copies are kept identical by hand --
 * `diff` them whenever either changes. It has no imports so that staying
 * identical is possible; keep it that way.
 *
 * Separate from `embedMessages.ts` on purpose. That file is the auth handshake
 * and is finished; this one will grow as methods are added, and they should not
 * have to move together.
 */

export const BRIDGE_RPC = 'veriform-embed:rpc';
export const BRIDGE_RPC_RESULT = 'veriform-embed:rpc-result';
export const BRIDGE_EVENT = 'veriform-embed:event';

/**
 * What the desktop app will answer. An allowlist rather than a switch default:
 * the page is untrusted, and an unknown method must be refused rather than
 * reaching whatever a fall-through would.
 */
export const BRIDGE_METHODS = [
  'engine.startRun',
  'engine.stopRun',
  'engine.activeRuns',
  'engine.listInstruments',
  'engine.addConnection',
  'engine.removeConnection',
  'engine.listConnections',
  'engine.providerConfigSchema',
  'code.run',
  'validation.run',
] as const;

export type BridgeMethod = typeof BRIDGE_METHODS[number];

export function isBridgeMethod(value: unknown): value is BridgeMethod {
  return typeof value === 'string' && (BRIDGE_METHODS as readonly string[]).includes(value);
}

/**
 * The only event pushed today: one per engine read of a run, once the desktop
 * app has saved it to veriform-web, until the run settles.
 */
export const BRIDGE_RUN_UPDATE_EVENT = 'engine.runUpdate';

export interface BridgeRpcRequest {
  type: typeof BRIDGE_RPC;
  requestId: string;
  method: BridgeMethod;
  params?: unknown;
}

export interface BridgeRpcResult {
  type: typeof BRIDGE_RPC_RESULT;
  requestId: string;
  result?: unknown;
  error?: { message: string };
}

export interface BridgeEvent {
  type: typeof BRIDGE_EVENT;
  event: string;
  payload: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Parent side: validate an RPC request claiming to come from the embedded frame.
 *
 * The origin and source checks are the same ones `readChildMessage` makes, for
 * the same reason -- this is the parent, which knows exactly which frame and
 * origin it is talking to. Returns null for anything that does not qualify,
 * including a method that is not on the allowlist.
 */
export function readRpcRequest(
  event: MessageEvent,
  expected: { origin: string; source: MessageEventSource | null | undefined },
): BridgeRpcRequest | null {
  if (!expected.source || event.source !== expected.source) return null;
  if (event.origin !== expected.origin) return null;
  if (!isRecord(event.data)) return null;
  if (event.data.type !== BRIDGE_RPC) return null;

  const { requestId, method, params } = event.data;
  if (typeof requestId !== 'string' || !requestId) return null;
  if (!isBridgeMethod(method)) return null;

  return { type: BRIDGE_RPC, requestId, method, params };
}

/**
 * Child side: validate a response or event from the desktop parent. No origin
 * check, for the reason given in `embedMessages.ts` -- a packaged parent is
 * served from `file://` and has none.
 */
export function readRpcResult(
  event: MessageEvent,
  expectedSource: MessageEventSource | null,
): BridgeRpcResult | BridgeEvent | null {
  if (!expectedSource || event.source !== expectedSource) return null;
  if (!isRecord(event.data)) return null;

  if (event.data.type === BRIDGE_RPC_RESULT) {
    const { requestId, result, error } = event.data;
    if (typeof requestId !== 'string' || !requestId) return null;
    return {
      type: BRIDGE_RPC_RESULT,
      requestId,
      result,
      error: isRecord(error) && typeof error.message === 'string' ? { message: error.message } : undefined,
    };
  }

  if (event.data.type === BRIDGE_EVENT) {
    const { event: name, payload } = event.data;
    if (typeof name !== 'string' || !name) return null;
    return { type: BRIDGE_EVENT, event: name, payload };
  }

  return null;
}

/** Parent -> child. Addressed by explicit origin, never '*'. */
export function postRpcResult(
  frame: Window | null | undefined,
  message: BridgeRpcResult | BridgeEvent,
  targetOrigin: string,
): void {
  frame?.postMessage(message, targetOrigin);
}
